import type { AddressValue, Field, FormValues, Section } from "../types";
import { ChipSelect } from "./ChipSelect";
import { TextInput } from "./TextInput";
import { TextArea } from "./TextArea";
import { DateOfBirth } from "./DateOfBirth";
import { AddressInput, emptyAddress } from "./AddressInput";
import { RadioGroup } from "./RadioGroup";
import { Consent } from "./Consent";

type Props = {
  index: number;
  total: number;
  section: Section;
  values: FormValues;
  errors: Record<string, string>;
  onChange: (id: string, value: FormValues[string]) => void;
};

const SECTION_OPENERS = [
  "Let's start with the basics.",
  "Tell us what's pulling you towards this.",
  "Now the fun bit — who are you outside of work?",
  "A few quick word-pictures.",
  "What can you sit alongside a young person through?",
  "Who could you click with?",
  "The practical side.",
  "One last reflection.",
];

export function SectionView({ index, total, section, values, errors, onChange }: Props) {
  return (
    <div className="section-anim" key={section.id}>
      <div className="section-header">
        <div className="eyebrow">
          Step {index + 1} of {total} · {SECTION_OPENERS[index] ?? ""}
        </div>
        <h1>{section.title}</h1>
        <p>{section.description}</p>
      </div>

      {section.fields.map((f) => (
        <div className="field" id={`field-${f.id}`} key={f.id}>
          {renderField(f, values, errors, onChange)}
        </div>
      ))}
    </div>
  );
}

function renderField(
  f: Field,
  values: FormValues,
  errors: Record<string, string>,
  onChange: (id: string, value: FormValues[string]) => void,
) {
  const err = errors[f.id];
  switch (f.type) {
    case "text":
      return (
        <TextInput
          id={f.id}
          label={f.label}
          value={(values[f.id] as string) ?? ""}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          placeholder={f.placeholder}
          autoComplete={autoCompleteFor(f.id)}
          error={err}
        />
      );
    case "email":
      return (
        <TextInput
          id={f.id}
          label={f.label}
          type="email"
          inputMode="email"
          autoComplete="email"
          value={(values[f.id] as string) ?? ""}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          error={err}
        />
      );
    case "phone":
      return (
        <TextInput
          id={f.id}
          label={f.label}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="64 21 234 5678"
          value={((values[f.id] as string) ?? "").replace(/^\++/, "")}
          onChange={(v) => onChange(f.id, v.replace(/^\++/, ""))}
          required={f.required}
          error={err}
        />
      );
    case "date":
      return (
        <DateOfBirth
          id={f.id}
          label={f.label}
          value={(values[f.id] as string) ?? ""}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          error={err}
        />
      );
    case "address": {
      const current = (values[f.id] as AddressValue) ?? emptyAddress();
      return (
        <AddressInput
          id={f.id}
          label={f.label}
          value={current}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          error={err}
        />
      );
    }
    case "textarea":
      return (
        <TextArea
          id={f.id}
          label={f.label}
          value={(values[f.id] as string) ?? ""}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          placeholder={f.placeholder}
          minLength={f.minLength}
          maxLength={f.maxLength}
          error={err}
        />
      );
    case "multi-select":
      return (
        <ChipSelect
          id={f.id}
          label={f.label}
          options={f.options}
          value={(values[f.id] as string[]) ?? []}
          onChange={(v) => onChange(f.id, v)}
          helper={f.helper}
          error={err}
        />
      );
    case "radio":
      return (
        <RadioGroup
          id={f.id}
          label={f.label}
          helper={f.helper}
          options={f.options}
          value={(values[f.id] as string) ?? ""}
          onChange={(v) => onChange(f.id, v)}
          required={f.required}
          error={err}
        />
      );
    case "consent":
      return (
        <Consent
          id={f.id}
          labelHtml={f.label}
          value={Boolean(values[f.id])}
          onChange={(v) => onChange(f.id, v)}
          error={err}
        />
      );
  }
}

function autoCompleteFor(id: string): string | undefined {
  if (id === "mentor_first_name") return "given-name";
  if (id === "mentor_last_name") return "family-name";
  if (id === "mentor_preferred_name") return "nickname";
  return undefined;
}
